/**
 * What the server accepts for every document / photo upload (both designs).
 * Mirrors the backend upload policy (app/core/unified_file_storage.py): the file type is decided
 * from its bytes — JPEG, PNG, WEBP, HEIC/HEIF or PDF — and it must not exceed UPLOAD_MAX_SIZE_MB
 * (10 MB by default). The server also refuses uploads once a request can no longer be edited.
 * Checking here only saves a pointless upload; the server stays the authority.
 */

export const MAX_UPLOAD_MB = 10;
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

const IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/pjpeg", "image/png", "image/webp", "image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence"];
const IMAGE_EXT = /\.(jpe?g|png|webp|heic|heif)$/i;
const PDF_EXT = /\.pdf$/i;

/** `accept` attribute for file inputs (extensions help pickers that do not know the HEIC MIME type). */
export const ACCEPT_IMAGES = "image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif";
export const ACCEPT_IMAGES_AND_PDF = `${ACCEPT_IMAGES},application/pdf,.pdf`;

export type UploadProblem = "empty" | "too_large" | "unsupported_type";

export function isPdfFile(file: { type?: string; name?: string }): boolean {
  return file.type === "application/pdf" || PDF_EXT.test(file.name || "");
}

export function isAllowedImage(file: { type?: string; name?: string }): boolean {
  const type = (file.type || "").toLowerCase();
  if (type) return IMAGE_TYPES.includes(type) || (type === "application/octet-stream" && IMAGE_EXT.test(file.name || ""));
  // Some browsers leave HEIC/HEIF (and other) files without a MIME type: fall back to the name.
  return IMAGE_EXT.test(file.name || "");
}

/** HEIC/HEIF cannot be drawn by most browsers (no preview, no cropping) but uploads fine. */
export function isHeic(file: { type?: string; name?: string }): boolean {
  return /image\/hei[cf]/i.test(file.type || "") || /\.hei[cf]$/i.test(file.name || "");
}

/** null when the file can be uploaded. */
export function checkUploadFile(file: { size: number; type?: string; name?: string }, opts: { allowPdf?: boolean } = {}): UploadProblem | null {
  const allowPdf = opts.allowPdf !== false;
  if (!(isAllowedImage(file) || (allowPdf && isPdfFile(file)))) return "unsupported_type";
  if (file.size <= 0) return "empty";
  if (file.size > MAX_UPLOAD_BYTES) return "too_large";
  return null;
}

type Lang = "ar" | "en";

/** One-line limits shown under every upload control. */
export function uploadLimitsText(lang: Lang, allowPdf = true): string {
  if (lang === "en") return allowPdf ? `Photo (JPG, PNG, WEBP, HEIC) or PDF · up to ${MAX_UPLOAD_MB} MB` : `Photo (JPG, PNG, WEBP, HEIC) · up to ${MAX_UPLOAD_MB} MB`;
  return allowPdf ? `صورة (JPG أو PNG أو WEBP أو HEIC) أو ملف PDF · حتى ${MAX_UPLOAD_MB} ميجابايت` : `صورة (JPG أو PNG أو WEBP أو HEIC) · حتى ${MAX_UPLOAD_MB} ميجابايت`;
}

export function uploadProblemText(problem: UploadProblem, lang: Lang, allowPdf = true): string {
  const en = lang === "en";
  switch (problem) {
    case "too_large":
      return en ? `The file is larger than ${MAX_UPLOAD_MB} MB. Choose a smaller file or take a new photo.` : `حجم الملف أكبر من ${MAX_UPLOAD_MB} ميجابايت. اختر ملفاً أصغر أو التقط صورة جديدة.`;
    case "empty":
      return en ? "The file is empty. Choose another file." : "الملف فارغ. اختر ملفاً آخر.";
    default:
      return en
        ? allowPdf
          ? "This file type is not accepted. Choose a photo (JPG, PNG, WEBP, HEIC) or a PDF."
          : "This file type is not accepted. Choose a photo (JPG, PNG, WEBP, HEIC)."
        : allowPdf
        ? "نوع الملف غير مقبول. اختر صورة (JPG أو PNG أو WEBP أو HEIC) أو ملف PDF."
        : "نوع الملف غير مقبول. اختر صورة (JPG أو PNG أو WEBP أو HEIC).";
  }
}

/** Translation key (LanguageContext) for a problem, for the classic screens. */
export function uploadProblemKey(problem: UploadProblem, allowPdf = true): string {
  if (problem === "too_large") return "file_size_must_be_less_than_10mb";
  if (problem === "empty") return "file_is_empty";
  return allowPdf ? "file_must_be_image_or_pdf" : "please_select_image_file";
}
